"use client";

import {
  Check,
  ChevronDown,
  ChevronRight,
  Globe,
  Loader2,
  Search,
  Star,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getInstantAudienceOptions,
  type InstantAudienceBuddy,
  updateInstantCloseFriends,
} from "@/actions/instant";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

export type AudienceValue = "public" | "private" | "buddies" | "close_friends";

export type AudienceOption = {
  value: AudienceValue;
  label?: string;
  description?: string;
};

type AudienceSelectorProps = {
  value: AudienceValue;
  onChange: (value: AudienceValue) => void;
  options: AudienceOption[];
  disabled?: boolean;
  /**
   * `inline`: a borderless brand-coloured text row (X's "Everyone can reply"),
   * for a composer's footer.
   */
  variant?: "compact" | "glass" | "wide" | "inline";
  tone?: "surface" | "dark";
  /** Trigger text in place of the selected option's label. */
  triggerLabel?: string;
  manageCloseFriends?: boolean;
  className?: string;
};

const DEFAULT_META: Record<
  AudienceValue,
  { label: string; description: string; icon: typeof Globe }
> = {
  public: {
    label: "Everyone",
    description: "Anyone on Hiive can see this.",
    icon: Globe,
  },
  private: {
    label: "Buddies only",
    description: "Only your buddies can see this.",
    icon: Users,
  },
  buddies: {
    label: "Buddies only",
    description: "Only your buddies can see this.",
    icon: Users,
  },
  close_friends: {
    label: "Close Friends",
    description: "Only buddies on your Close Friends list.",
    icon: Star,
  },
};

const resolvedOption = (option: AudienceOption) => ({
  ...DEFAULT_META[option.value],
  ...option,
});

export function AudienceSelector({
  value,
  onChange,
  options,
  disabled = false,
  variant = "compact",
  tone = "surface",
  manageCloseFriends = true,
  triggerLabel,
  className,
}: AudienceSelectorProps) {
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);
  const selected = selectedOption
    ? resolvedOption(selectedOption)
    : { ...DEFAULT_META[value], value };
  const SelectedIcon = selected.icon;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={`Audience: ${selected.label}`}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition active:scale-[0.98] disabled:opacity-50",
          variant === "compact" && "h-9 px-3 text-xs",
          variant === "glass" &&
            "h-11 border-white/20 bg-white/12 px-4 text-sm text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] backdrop-blur-xl",
          variant === "wide" && "h-11 w-full px-4 text-sm",
          variant === "inline" &&
            "h-8 gap-1.5 border-0 bg-transparent px-0 text-[14px] text-brand",
          tone === "surface" &&
            variant !== "glass" &&
            variant !== "inline" &&
            "border-border/70 bg-surface/82 text-text-primary shadow-sm backdrop-blur-xl hover:bg-surface",
          tone === "dark" &&
            variant !== "glass" &&
            variant !== "inline" &&
            "border-white/15 bg-white/10 text-white backdrop-blur-xl hover:bg-white/15",
          value === "close_friends" &&
            (variant === "inline"
              ? "text-emerald-600 dark:text-emerald-400"
              : "border-emerald-500/30 bg-emerald-500/14 text-emerald-600 dark:text-emerald-300"),
          className,
        )}
      >
        <SelectedIcon className="h-4 w-4" />
        <span>{triggerLabel ?? selected.label}</span>
        {variant === "inline" ? null : (
          <ChevronDown className="h-4 w-4 opacity-70" />
        )}
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] mx-auto max-w-lg border-border bg-background pb-[max(env(safe-area-inset-bottom),1rem)] dark:border-white/10"
        >
          <DrawerHeader className="text-left">
            <DrawerTitle>Share with</DrawerTitle>
            <DrawerDescription>
              Buddies only is the default. You can change it for this item.
            </DrawerDescription>
          </DrawerHeader>
          <div className="space-y-1 px-3 pb-2">
            {options.map((option) => {
              const meta = resolvedOption(option);
              const Icon = meta.icon;
              const active = option.value === value;
              return (
                <div
                  key={option.value}
                  className={cn(
                    "flex items-center rounded-2xl border transition",
                    active
                      ? "border-brand/30 bg-brand/8"
                      : "border-transparent hover:bg-surface-alt",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left"
                  >
                    <span
                      className={cn(
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
                        option.value === "close_friends"
                          ? "bg-emerald-500 text-white"
                          : "bg-brand text-white",
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-text-primary">
                        {meta.label}
                      </span>
                      <span className="block text-sm text-text-secondary">
                        {meta.description}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
                        active
                          ? "border-brand bg-brand text-white"
                          : "border-border",
                      )}
                    >
                      {active ? <Check className="h-4 w-4" /> : null}
                    </span>
                  </button>
                  {option.value === "close_friends" && manageCloseFriends ? (
                    <button
                      type="button"
                      aria-label="Edit Close Friends"
                      onClick={() => setManageOpen(true)}
                      className="mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-text-secondary hover:bg-surface hover:text-text-primary"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>

      {manageCloseFriends ? (
        <CloseFriendsManagerDrawer
          open={manageOpen}
          onOpenChange={setManageOpen}
        />
      ) : null}
    </>
  );
}

function CloseFriendsManagerDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [buddies, setBuddies] = useState<InstantAudienceBuddy[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void getInstantAudienceOptions()
      .then((result) => {
        if (cancelled) return;
        if (result.ok) setBuddies(result.data.buddies);
        else toast.error(result.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return buddies;
    return buddies.filter(
      (buddy) =>
        buddy.displayName.toLowerCase().includes(needle) ||
        buddy.username?.toLowerCase().includes(needle),
    );
  }, [buddies, query]);

  const save = async () => {
    setSaving(true);
    const result = await updateInstantCloseFriends({
      userIds: buddies
        .filter((buddy) => buddy.isCloseFriend)
        .map((buddy) => buddy.userId),
    });
    setSaving(false);
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    toast.success("Close Friends updated");
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} nested>
      <DrawerContent
        overlayClassName="z-[1290]"
        className="z-[1300] h-[88dvh] max-h-[88dvh] bg-background"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="px-4 pt-3 text-center">
            <DrawerTitle>Close Friends</DrawerTitle>
            <DrawerDescription>Nobody is notified.</DrawerDescription>
          </div>

          <div className="mx-4 mt-3 flex items-center gap-2 rounded-full bg-surface-alt px-4">
            <Search className="h-4 w-4 text-text-tertiary" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search buddies"
              className="h-11 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {loading ? (
              <div className="flex h-40 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-brand" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-12 text-center text-sm text-text-secondary">
                {buddies.length === 0
                  ? "Add buddies first to build your Close Friends list."
                  : "No buddies match your search."}
              </p>
            ) : (
              filtered.map((buddy) => (
                <button
                  key={buddy.userId}
                  type="button"
                  onClick={() =>
                    setBuddies((current) =>
                      current.map((item) =>
                        item.userId === buddy.userId
                          ? { ...item, isCloseFriend: !item.isCloseFriend }
                          : item,
                      ),
                    )
                  }
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left hover:bg-surface-alt"
                >
                  {buddy.avatarUrl ? (
                    // biome-ignore lint/performance/noImgElement: compact buddy avatar
                    <img
                      src={buddy.avatarUrl}
                      alt=""
                      className="h-11 w-11 rounded-[30%] object-cover"
                    />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-[30%] bg-surface-alt font-semibold">
                      {buddy.displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      {buddy.displayName}
                    </span>
                    {buddy.username ? (
                      <span className="block truncate text-xs text-text-secondary">
                        @{buddy.username}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full border",
                      buddy.isCloseFriend
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-border",
                    )}
                  >
                    {buddy.isCloseFriend ? <Check className="h-4 w-4" /> : null}
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-border px-4 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <button
              type="button"
              onClick={() => void save()}
              disabled={loading || saving}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand font-semibold text-white disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Close Friends
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
